import { IEnvironment } from "../interfaces/IEnvironment";
import AuthenticationService, { TokenWithExpiration } from "./AuthenticationService";

const TOKEN_STORAGE_NAME = "background-messages-token-cache";
const UpdateIntervalInMilliseconds = 60*1000;
type OnUpdateEvent = () => void;

export default class BackgroundMessageService {
  private intervalId: NodeJS.Timer;
  private lastUpdatedTime: number = Date.now();

  public beginBackgroundCheck(environment: IEnvironment, tenant: string, instance: string, updateEvent: OnUpdateEvent) : void {
    if (!!this.intervalId) {
      return;
    }
    this.intervalId = setInterval(() => this.onInterval(environment, tenant, instance, updateEvent), UpdateIntervalInMilliseconds);
  }

  public stopBackgroundCheck() : void {
    if (!this.intervalId) {
      throw new Error("The begin method have not been called");
    }
    clearInterval(this.intervalId);
  }

  private async onInterval(environment: IEnvironment, tenant: string, instance: string, updateEvent: OnUpdateEvent): Promise<void> {
    try {
      const storageTokenAndExpirationTime = sessionStorage.getItem(TOKEN_STORAGE_NAME);
      const tokenAndExpirationTime: TokenWithExpiration =  storageTokenAndExpirationTime ? JSON.parse(storageTokenAndExpirationTime) : null;
      let sharePointToken = tokenAndExpirationTime?.token;
      if (!sharePointToken || new Date(Date.parse(tokenAndExpirationTime.validTo)) < new Date()) {
        const sharePointTokenAndExpirationTime = await AuthenticationService.getSharepointTokenWithExpiration(environment, tenant);
        sharePointToken = sharePointTokenAndExpirationTime.token;
        sessionStorage.setItem(TOKEN_STORAGE_NAME, JSON.stringify(sharePointTokenAndExpirationTime));
      }
    
      const lastMessageTime = await this.getSharePointLastMessageDate(sharePointToken, tenant, instance);
      if (lastMessageTime > this.lastUpdatedTime) {
        updateEvent();
      }
      this.lastUpdatedTime = isNaN(lastMessageTime) ? this.lastUpdatedTime : lastMessageTime;
    } catch (err) {
      // Should not break messages if we are unable to get messages - try again later.
      console.warn("Messages was unable to be loaded: " + JSON.stringify(err));
    }
  }

  private async getSharePointLastMessageDate(sharePointToken: string, tenant: string, instance: string): Promise<number> {
    /*
     * Get one item ordered by update date. Filter items that have end date before the last updated date.
     * In case of targeting the update might trigger, but the results will be the same and
     * that will depend on the amount of updates (they shouldn't be too many)
     */

    const filter = `$top=1&$select=Modified&$filter=IAMessageStartTime le datetime'${new Date(this.lastUpdatedTime).toISOString()}' and (IAMessageEndTime ge datetime'${new Date(this.lastUpdatedTime).toISOString()}' or IAMessageEndTime eq null) and IADraft eq 0&$orderby=Modified%20desc`
    return new Promise<number>((resolve, reject) => {
      fetch(`https://${tenant}.sharepoint.com/sites/IntraActive-Messages/_api/web/lists/getByTitle('${this.getInstanceListFromName(instance)}')/items?${filter}`, {
        method: "GET",
        mode: "cors",
        headers: new Headers({
          "Accept": "application/json;odata=verbose",
          "Authorization": `bearer ${sharePointToken}`
        })
      })
      .then(function (response) {
        if (!response.ok) {
          reject(response.text());
        }
        return response.json();
      }).then((response: { d: { results: [ { Modified: string } ] } }) => {
        if (response.d.results.length > 0) {
          resolve(Date.parse(response.d.results[0].Modified))
        } else {
          resolve(null);
        }
      }).catch((reason) => {
        reject(reason);
      });
    });
  }

  private getInstanceListFromName(instance: string): string {
    if (instance.toLowerCase() == "default") {
      return "Messages";
    }
    return `${instance} - Messages`;
  }
}